import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { provideHttpClient, withFetch } from '@angular/common/http';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { TextFieldComponent } from './component/text-field/text-field.component';
import { FormlyModule } from '@ngx-formly/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FormComponent } from './form/form.component';
import { TextAreaComponent } from './component/text-area/text-area.component';
import { NumberInputComponent } from './component/number-input/number-input.component';
import { CurrencyPipe } from './pipe/currency.pipe';
import { DatePickerComponent } from './component/date-picker/date-picker.component';
import { CheckboxComponent } from './component/checkbox/checkbox.component';
import { RadioComponent } from './component/radio/radio.component';
import { SingleSelectComponent } from './component/single-select/single-select.component';
import { MultiSelectComponent } from './component/multi-select/multi-select.component';
import { AddressComponent } from './component/composite/address/address.component';
import { FieldWrapperComponent } from './component/wrappers/field-wrapper/field-wrapper.component';
import { LabelComponent } from './component/label/label.component';
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { validateAddress } from './form/validators/validate-address';
import { LoginExistingComponent } from './component/composite/login-existing/login-existing.component';
import { validateLogin } from './form/validators/validate-login';
import { LoginSectionComponent } from './component/sections/login-section/login-section.component';
import { GeneralSectionComponent } from './component/sections/general-section/general-section.component';
import { NameFieldComponent } from "./component/composite/name-field/name-field.component";
import { validateName } from "./form/validators/validate-name";
import { LoginNewComponent } from './component/composite/login-new/login-new.component';
import { validateLoginNew } from './form/validators/validate-login-new';
import { FileFieldComponent } from './component/file-field/file-field.component';
import { EventSectionComponent } from './component/sections/event-section/event-section.component';
import { PaymentSectionComponent } from './component/sections/payment-section/payment-section.component';
import { MembershipSectionComponent } from './component/sections/membership-section/membership-section.component';
import { GenericHeaderWrapperComponent } from './component/wrappers/generic-header-wrapper/generic-header-wrapper.component';
import { RegistrantWrapperComponent } from './component/wrappers/registrant-wrapper/registrant-wrapper.component';
import { EventButtonComponent } from './component/event-button/event-button.component';
import { CaptchaComponent } from './component/captcha/captcha.component';
import { CreateLinkedProfileButtonComponent } from './component/create-linked-profile-button/create-linked-profile-button.component';
import { validateNumeric } from "./form/validators/validate-numeric";
import { CvvComponent } from './component/cvv/cvv.component';
import { EftCheckExampleComponent } from './component/eft-check-example/eft-check-example.component';
import { validateAccountNumbers } from "./form/validators/validate-account-numbers";
import { EftAuthorizeAgreementComponent } from './component/eft-authorize-agreement/eft-authorize-agreement.component';
import { CountdownTimerComponent } from './component/countdown-timer/countdown-timer.component';
import { validateEmail } from "./form/validators/validate-email";
import { RestartFormModalComponent } from './component/restart-form-modal/restart-form-modal.component';
import { SummarySectionComponent } from './component/sections/summary-section/summary-section.component';
import { ReviewPanelSectionComponent } from './component/sections/review-panel-section/review-panel-section.component';
import { GenericModalComponent } from './component/generic-modal/generic-modal.component';
import { SectionErrorMessageComponent } from './component/section-error-message/section-error-message.component';
import { SummarySimpleTextComponent } from './component/summary/summary-simple-text/summary-simple-text.component';
import { SummaryPriceFieldComponent } from './component/summary/summary-price-field/summary-price-field.component';
import { SummaryAddressFieldComponent } from './component/summary/summary-address-field/summary-address-field.component';
import { SummaryFileFieldComponent } from './component/summary/summary-file-field/summary-file-field.component';
import { SummaryMembershipSectionComponent } from './component/summary/summary-membership-section/summary-membership-section.component';
import { SummaryEventSectionComponent } from './component/summary/summary-event-section/summary-event-section.component';
import { SummaryGeneralSectionComponent } from './component/summary/summary-general-section/summary-general-section.component';
import { SummaryReviewPanelSectionComponent } from './component/summary/summary-review-panel-section/summary-review-panel-section.component';
import { SummarySelectFieldComponent } from './component/summary/summary-select-field/summary-select-field.component';
import { EmailPhoneComponent } from './component/composite/email-phone/email-phone.component';
import { HtmlDecodePipe } from './pipe/html-decode.pipe';
import { WhiteSpaceComponent } from './component/white-space/white-space.component';
import { LineSeparatorComponent } from './component/line-separator/line-separator.component';
import { ShoelaceReadonlyDirective } from './directive/shoelace-readonly.directive';

@NgModule({
  declarations: [
    AppComponent,
    TextFieldComponent,
    FormComponent,
    TextAreaComponent,
    NumberInputComponent,
    CurrencyPipe,
    DatePickerComponent,
    CheckboxComponent,
    RadioComponent,
    SingleSelectComponent,
    MultiSelectComponent,
    AddressComponent,
    FieldWrapperComponent,
    GeneralSectionComponent,
    LabelComponent,
    NameFieldComponent,
    LoginExistingComponent,
    LoginSectionComponent,
    LoginNewComponent,
    LoginSectionComponent,
    FileFieldComponent,
    PaymentSectionComponent,
    EventSectionComponent,
    RegistrantWrapperComponent,
    EventButtonComponent,
    MembershipSectionComponent,
    GenericHeaderWrapperComponent,
    CaptchaComponent,
    CreateLinkedProfileButtonComponent,
    CvvComponent,
    EftCheckExampleComponent,
    EftAuthorizeAgreementComponent,
    CountdownTimerComponent,
    ReviewPanelSectionComponent,
    SummarySectionComponent,
    RestartFormModalComponent,
    GenericModalComponent,
    SectionErrorMessageComponent,
    SummarySimpleTextComponent,
    SummaryPriceFieldComponent,
    SummaryAddressFieldComponent,
    SummaryFileFieldComponent,
    SummaryMembershipSectionComponent,
    SummaryEventSectionComponent,
    SummaryGeneralSectionComponent,
    SummaryReviewPanelSectionComponent,
    SummarySelectFieldComponent,
    EmailPhoneComponent,
    HtmlDecodePipe,
    WhiteSpaceComponent,
    LineSeparatorComponent,
    ShoelaceReadonlyDirective
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    ReactiveFormsModule,
    FormlyModule.forRoot({
      validators: [
        { name: 'addressInvalid', validation: validateAddress },
        { name: 'nameRequired', validation: validateName },
        { name: 'loginInvalid', validation: validateLogin },
        { name: 'loginNewInvalid', validation: validateLoginNew },
        { name: 'mustBeNumeric', validation: validateNumeric },
        { name: 'mustMatchAccountNumbers', validation: validateAccountNumbers },
        { name: 'email', validation: validateEmail }
      ]
    }),
    MatProgressSpinnerModule
  ],
  providers: [
    provideHttpClient(withFetch()),
    // providers to inject values from the Joomla backend rendered page
    { provide: 'CAPTCHA_KEY', useValue: (<any> window)._ANGULAR_CAPTCHA_KEY },
    { provide: 'CURRENT_FORM_ID', useValue: (<any> window)._CURRENT_FORM_ID },
    { provide: 'TAKE_FORM', useValue: (<any> window)._TAKE_FORM },
    { provide: 'TIMEZONE_SETTING', useValue: (<any> window)._TIMEZONE_SETTING },
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }
